import Alpine from 'alpinejs';
/*
 * lottie-web is ~300 KiB and only a few components use it, so it is loaded on demand: the first
 * call fetches it (Vite splits it into its own chunk) and later calls reuse the same promise.
 * createLottieAnimation therefore returns a Promise<AnimationItem|null>.
 */
let lottiePromise = null;

/**
 * Resolve once the page has finished loading and the main thread is idle, so parsing and running
 * the library (about 1.3 s of CPU on a mid-range phone) never competes with first paint or the
 * Largest Contentful Paint image. Falls back to a short timeout where requestIdleCallback is missing.
 */
function whenIdle() {
    return new Promise((resolve) => {
        const idle = () => {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => resolve(), { timeout: 1500 });
            } else {
                setTimeout(resolve, 200);
            }
        };
        if (document.readyState === 'complete') {
            idle();
        } else {
            window.addEventListener('load', idle, { once: true });
        }
    });
}

function loadLottie() {
    if (!lottiePromise) {
        lottiePromise = whenIdle().then(() => import('lottie-web')).then((module) => module.default);
    }
    return lottiePromise;
}

const DEFAULT_OPTIONS = {
    loop: true,
    autoplay: true,
    renderer: 'svg',
    playOnView: false,
    threshold: 0.2,
    rootMargin: '0px',
};

function buildAnimationConfig(container, options, autoplay) {
    const config = {
        container,
        renderer: options.renderer,
        loop: options.loop,
        autoplay,
    };

    if (options.animationData) {
        config.animationData = options.animationData;
    } else if (options.path) {
        config.path = options.path;
    }

    // [start, end] frame range to play instead of the whole file, e.g. to stop a non-looping
    // animation on its finished pose when the export carries a few erase frames after it.
    if (Array.isArray(options.initialSegment) && options.initialSegment.length === 2) {
        config.initialSegment = options.initialSegment;
    }

    return config;
}

/**
 * Create a Lottie animation instance.
 *
 * @param {HTMLElement} container
 * @param {object} options
 * @returns {Promise<import('lottie-web').AnimationItem|null>}
 */
export async function createLottieAnimation(container, options = {}) {
    const merged = { ...DEFAULT_OPTIONS, ...options };

    if (!merged.path && !merged.animationData) {
        console.warn('lottie: missing path or animationData', container);
        return null;
    }

    const lottie = await loadLottie();

    return lottie.loadAnimation(
        buildAnimationConfig(container, merged, merged.autoplay)
    );
}

/**
 * Alpine.js data component for Lottie animations.
 *
 * Usage in PHP templates:
 *
 * <div
 *   class="my-component__animation"
 *   x-data="lottie(<?php echo htmlspecialchars(json_encode([
 *     'path' => get_template_directory_uri() . '/animations/example.json',
 *     'loop' => true,
 *     'autoplay' => false,
 *     'playOnView' => true,
 *     'threshold' => 0.5,
 *     'rootMargin' => '0px 0px -50% 0px',
 *     'initialSegment' => [0, 120], // optional frame range
 *   ]), ENT_QUOTES, 'UTF-8'); ?>)"
 * ></div>
 */
Alpine.data('lottie', (initialOptions = {}) => ({
    animation: null,
    observer: null,
    /*
     * Declared here on purpose. Inside Alpine methods `this` is a merged scope proxy: assigning a
     * property the component does not own lands on the outermost scope, which every lottie() on
     * the page shares. An undeclared loadToken was one counter for all animations, so each
     * finished load saw a "newer" token and discarded itself, leaving only the last one playing.
     */
    loadToken: 0,
    /*
     * The playback state a caller asked for before the library or file had loaded (null = leave it
     * to the autoplay option), applied once the animation exists so play()/pause() are safe to call
     * at any time, e.g. from a carousel deciding which slide's animation should run.
     */
    requestedPlayback: null,
    options: { ...DEFAULT_OPTIONS, ...initialOptions },

    init() {
        const container = this.$refs.container || this.$el;

        if (!this.options.path && !this.options.animationData) {
            console.warn('lottie: missing path or animationData', container);
            return;
        }

        if (this.options.playOnView) {
            this.initPlayOnView(container);
            return;
        }

        // Autoplaying animations still wait until they are near the viewport: an animation far
        // down the page costs nothing until the visitor scrolls towards it.
        this.initLoadNearView(container);
    },

    initLoadNearView(container) {
        if (!('IntersectionObserver' in window)) {
            this.loadAnimation(container);
            return;
        }

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }
                this.loadAnimation(container);
                this.observer?.disconnect();
                this.observer = null;
            });
        }, { rootMargin: '300px 0px' });

        this.observer.observe(this.$el);
    },

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        this.destroyAnimation();
    },

    initPlayOnView(container) {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                if (!this.animation) {
                    this.loadAnimation(container, true);
                } else {
                    this.animation.play();
                }

                this.observer?.disconnect();
                this.observer = null;
            });
        }, {
            threshold: this.options.threshold,
            rootMargin: this.options.rootMargin,
        });

        this.observer.observe(this.$el);
    },

    loadAnimation(container, autoplay = this.options.autoplay) {
        this.destroyAnimation();
        // The library loads asynchronously; a later load or destroy wins over an earlier one.
        const token = ++this.loadToken;
        createLottieAnimation(container, {
            ...this.options,
            autoplay,
        }).then((animation) => {
            if (!animation) {
                return;
            }
            if (token !== this.loadToken) {
                animation.destroy();
                return;
            }
            this.animation = animation;
            if (this.requestedPlayback === 'play') {
                animation.play();
            } else if (this.requestedPlayback === 'pause') {
                animation.pause();
            } else if (this.requestedPlayback === 'stop') {
                animation.stop();
            }
        });
    },

    destroyAnimation() {
        this.loadToken += 1;
        if (this.animation) {
            this.animation.destroy();
            this.animation = null;
        }
    },

    play() {
        this.requestedPlayback = 'play';
        this.animation?.play();
    },

    pause() {
        this.requestedPlayback = 'pause';
        this.animation?.pause();
    },

    stop() {
        this.requestedPlayback = 'stop';
        this.animation?.stop();
    },
}));
